<template>
  <div>
    <pack-info :packId="packId"></pack-info>
    <v-container>
      <v-row class="och-topMargin--packInfoOffset">
        <v-col sm="12" md="8">
          <h1 class="font-weight-thin mb-2">Edit Pack Lead</h1>

          <v-card class="elevation-0 pa-4" :loading="packDataIsLoading">
            <v-card-title class="subtitle font-weight-thin">
              Current Pack Lead:
              <span v-if="packDataIsLoading"><v-icon>fas fa-spin fa-spinner</v-icon></span>
              <span v-else class="ml-1">{{ currentPackLeadSnapshot || "unassigned" }}</span>
            </v-card-title>
            <v-card-text>
              <v-form ref="form" v-show="!packDataIsLoading">
                <v-row>
                  <v-col sm="12" md="8">
                    <user-picker
                      :clearIcon="'fa fa-times'"
                      attributeName="memberPackLead"
                      data-cy="member-pack-lead-user-picker"
                      data-test="member-pack-lead-user-picker"
                      label="Pack Lead"
                      outlined
                      v-model="memberPackLead"
                      :excludeRoles="excludedPackLeadRoles"
                    ></user-picker>

                    <v-alert type="info" text dense class="mt-3">
                      Only member users with Pack Lead permissions (Admin or Manager roles) can be assigned. View-only
                      users and OCHIN staff are excluded from this list.
                    </v-alert>

                    <v-card-actions>
                      <v-btn
                        @click="cancel"
                        outlined
                        data-cy="member-pack-lead-cancel"
                        data-test="member-pack-lead-cancel"
                      >
                        Cancel
                      </v-btn>
                      <v-tooltip
                        bottom
                        :disabled="!isDisabledDueToNoChanges"
                        data-cy="member-pack-lead-save-tooltip"
                        data-test="member-pack-lead-save-tooltip"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            v-bind="attrs"
                            v-on="on"
                            class="ml-2"
                            :aria-disabled="isDisabledDueToNoChanges ? 'true' : undefined"
                            :aria-describedby="
                              isDisabledDueToNoChanges ? 'member-pack-lead-save-tooltip-content' : undefined
                            "
                            :tabindex="isDisabledDueToNoChanges ? 0 : -1"
                            data-cy="member-pack-lead-save-tooltip-activator"
                          >
                            <v-btn
                              @click="save"
                              :disabled="isSaveDisabled"
                              color="primary"
                              data-cy="member-pack-lead-save"
                              data-test="member-pack-lead-save"
                            >
                              Save
                            </v-btn>
                          </span>
                        </template>
                        <span
                          id="member-pack-lead-save-tooltip-content"
                          data-cy="member-pack-lead-save-tooltip-content"
                          >{{ saveButtonTooltip }}</span
                        >
                      </v-tooltip>
                    </v-card-actions>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import UserPicker from "@/components/userPicker";
import GenericLoader from "@/components/GenericLoader";
import PackInfo from "@/components/packInfo";
import { mapActions, mapState } from "vuex";

export default {
  name: "MemberPackEdit",
  components: {
    UserPicker,
    GenericLoader,
    PackInfo,
  },
  data() {
    return {
      loading: false,
      packDataIsLoading: false,
      memberPackLead: null,
      currentPackLeadSnapshot: null,
      excludedPackLeadRoles: [
        "memberViewOnly",
        "memberAssignee",
        "ochinAdmin",
        "ochinQaLead",
        "ochinQaAnalyst",
        "ochinBiAnalyst",
        "ochinViewOnly",
        "ochinAccountManager",
      ],
    };
  },
  computed: {
    packId() {
      return parseInt(this.$route.params.packId, 10);
    },
    serviceAreaId() {
      return parseInt(this.$route.params.serviceAreaId, 10);
    },
    hasChanges() {
      const currentValue = this.memberPackLead || null;
      const originalValue = this.memberPack.memberPackLead || null;
      return currentValue !== originalValue;
    },
    isSaveDisabled() {
      return !this.hasChanges || this.loading;
    },
    isDisabledDueToNoChanges() {
      return !this.hasChanges && !this.loading;
    },
    saveButtonTooltip() {
      return !this.hasChanges ? "No changes to save" : "";
    },
    ...mapState({
      memberPack: state => state.memberPack.memberPack,
    }),
  },
  async created() {
    this.packDataIsLoading = true;
    try {
      await this.fetchMemberPack({ packId: this.packId, serviceArea: this.serviceAreaId });
      this.currentPackLeadSnapshot = this.memberPack.memberPackLeadDisplayName;
      this.memberPackLead = this.memberPack.memberPackLead;
    } catch (error) {
      console.error("Error fetching member pack:", error);
      this.$notifyError({ message: "There was an error loading Pack Lead." });
    } finally {
      this.packDataIsLoading = false;
    }
  },
  methods: {
    ...mapActions({
      fetchMemberPack: "memberPack/fetchMemberPack",
      updateMemberPack: "memberPack/updateMemberPack",
    }),
    async save() {
      try {
        this.loading = true;
        let result = await this.updateMemberPack({
          packId: this.packId,
          serviceArea: this.serviceAreaId,
          memberPackLead: this.memberPackLead,
        });
        this.$notifySuccess("Successfully saved Pack Lead.");
        this.goToDashboard();
        return result;
      } catch (e) {
        console.error("Failed to save Pack Lead", e);
        this.$notifyError({ message: "There was an error saving Pack Lead." });
        throw e;
      } finally {
        this.loading = false;
      }
    },

    cancel() {
      this.goToDashboard();
    },

    goToDashboard() {
      this.$router.push({ name: "uatDashboard", params: { packId: this.packId, serviceArea: this.serviceAreaId } });
    },
  },
};
</script>
